/**
 * The emoji the product knows about: the faces people wear, and the palette
 * they type from.
 *
 * Curated, not the whole of Unicode. A full set is ten times this, arrives as
 * a dependency, and pays for itself in nothing: most of it is flags, clocks
 * and keycaps nobody reaches for, a good part of it renders as a hollow box
 * on the machines learners actually have, and a picker with four thousand
 * cells in it is a search box with extra steps. What is here is what people
 * use in a room about work: the expressions, the hands, the animals, and the
 * dozen symbols an engineer reaches for when something is on fire.
 *
 * One table, both ends of the wire. The server needs it to check that a face
 * is a face — a browser may claim one of these and nothing else, which is the
 * rule that keeps an arbitrary URL out of everyone's member list (see
 * `cleanFace`) — and the tab needs it to draw the picker and to turn
 * `:shipit:` into something. A disagreement between the two would be a face
 * the room refuses and the picker offers, so there is one table.
 *
 * Every entry is one string: the character, its shortcode, then whatever else
 * someone might search for. Written that way because a list of two hundred
 * objects with three keys each is unreadable, and this is a data file that
 * people will want to edit.
 *
 * Skin tones are deliberately absent. Offering five variants of every hand
 * quintuples the picker to say something the product has no business asking
 * a learner to declare; the default yellow is the one Unicode calls
 * non-human, and it is what everyone gets.
 */
/** A group's heading in one language. */
export function groupName(group, locale = 'en') {
    return locale === 'ar' ? group.nameAr : group.name;
}
/* ── The table ──────────────────────────────────────────────────────────── */
// `English heading | Arabic heading | rows`. The headings are the only prose
// in this file: everything after them is a character and the words that find
// it, and both of those are typed rather than read (docs/i18n.md §1).
const TABLE = [
    [
        'Smileys',
        'وجوه',
        [
            '😀 grinning grin happy smile',
            '😃 smiley happy joy',
            '😄 smile happy joy laugh',
            '😁 grin beaming happy',
            '😆 laughing lol haha',
            '😅 sweat_smile nervous phew relief',
            '🤣 rofl rolling laughing dying',
            '😂 joy tears laughing crying',
            '🙂 slight_smile smile fine ok',
            '🙃 upside_down irony sarcasm',
            '😉 wink joke',
            '😊 blush shy pleased',
            '😇 innocent halo angel',
            '😌 relieved calm content',
            '😍 heart_eyes love adore',
            '😘 kissing_heart kiss',
            '😎 sunglasses cool',
            '🤩 star_struck amazed wow',
            '🥳 partying celebrate party',
            '🤓 nerd glasses study',
            '🧐 monocle inspect scrutiny',
            '🤔 thinking hmm consider',
            '🤨 raised_eyebrow doubt suspicious',
            '😐 neutral blank meh',
            '😑 expressionless blank done',
            '😶 no_mouth silent speechless',
            '🙄 roll_eyes eyeroll whatever',
            '😏 smirk sly',
            '😬 grimacing awkward yikes eek',
            '😮 open_mouth surprised oh',
            '😯 hushed surprised',
            '😲 astonished shocked',
            '🤯 exploding_head mindblown wow',
            '😳 flushed embarrassed oops',
            '🥺 pleading please beg',
            '😢 cry sad tear',
            '😭 sob crying bawling',
            '😤 triumph huff steam determined',
            '😠 angry mad',
            '😡 rage furious',
            '🤬 cursing swearing censored',
            '😱 scream fear horror',
            '😰 anxious worried sweat',
            '😥 sad disappointed relieved',
            '😓 downcast sweat tired',
            '🥱 yawning bored tired',
            '😴 sleeping zzz asleep',
            '🤒 sick thermometer ill',
            '🤕 bandage hurt injured',
            '🤢 nauseated sick gross',
            '🤮 vomiting sick',
            '🥴 woozy dizzy drunk',
            '😵 dizzy knocked_out dead',
            '🤠 cowboy yeehaw',
            '🥸 disguise incognito',
            '😈 smiling_imp devil mischief',
            '💀 skull dead rip',
            '👻 ghost boo spooky',
            '👽 alien ufo',
            '🤖 robot bot ai',
            '🤡 clown',
            '💩 poop crap garbage',
        ],
    ],
    [
        'Hands',
        'أيادٍ',
        [
            '👍 thumbsup yes agree lgtm +1',
            '👎 thumbsdown no disagree -1',
            '👌 ok perfect fine',
            '🤌 pinched chef italian',
            '✌️ victory peace',
            '🤞 fingers_crossed hope luck',
            '🤝 handshake deal agreed',
            '👏 clap applause bravo',
            '🙌 raised_hands praise celebrate',
            '🙏 pray thanks please namaste',
            '💪 muscle strong flex',
            '🫡 salute yes_sir respect',
            '🤙 call_me shaka',
            '👋 wave hi hello bye',
            '✋ hand stop halt',
            '🖐️ raised_hand five',
            '👆 point_up above',
            '👇 point_down below',
            '👈 point_left',
            '👉 point_right this',
            '🫵 point_at_you you',
            '✊ fist solidarity',
            '👊 punch bump bro',
            '🤘 rock_on horns metal',
            '🖖 vulcan spock live_long',
            '🤷 shrug dunno idk',
            '🤦 facepalm ugh oops',
            '🙇 bow sorry apology',
            '💁 info tipping_hand sassy',
            '🙋 raising_hand question me',
            '🙆 ok_person yes',
            '🙅 no_good stop nope',
            '👀 eyes looking watching review',
            '🧠 brain smart think',
        ],
    ],
    [
        'Hearts',
        'قلوب',
        [
            '❤️ heart love red',
            '🧡 orange_heart',
            '💛 yellow_heart',
            '💚 green_heart',
            '💙 blue_heart',
            '💜 purple_heart',
            '🖤 black_heart',
            '🤍 white_heart',
            '💔 broken_heart sad',
            '💖 sparkling_heart',
            '💘 cupid arrow love',
            '💯 hundred perfect score full_marks',
        ],
    ],
    [
        'Animals',
        'حيوانات',
        [
            '🐶 dog puppy',
            '🐱 cat kitten',
            '🐭 mouse',
            '🐹 hamster',
            '🐰 rabbit bunny',
            '🦊 fox',
            '🐻 bear',
            '🐼 panda',
            '🐨 koala',
            '🐯 tiger',
            '🦁 lion',
            '🐮 cow',
            '🐷 pig',
            '🐸 frog',
            '🐵 monkey',
            '🙈 see_no_evil monkey hide',
            '🐧 penguin',
            '🐦 bird',
            '🦆 duck',
            '🦉 owl night wise',
            '🦇 bat',
            '🐺 wolf',
            '🐗 boar',
            '🐴 horse',
            '🦄 unicorn magic',
            '🐝 bee',
            '🐛 bug caterpillar defect',
            '🦋 butterfly',
            '🐌 snail slow',
            '🐞 ladybug',
            '🐜 ant',
            '🕷️ spider',
            '🐢 turtle slow',
            '🐍 snake python',
            '🦎 lizard',
            '🦖 t_rex dinosaur',
            '🐙 octopus',
            '🦑 squid',
            '🦀 crab rust',
            '🐡 blowfish',
            '🐬 dolphin',
            '🐳 whale docker',
            '🦈 shark',
            '🐊 crocodile',
            '🐘 elephant',
            '🦒 giraffe',
            '🦔 hedgehog',
            '🐑 sheep',
            '🐓 rooster',
            '🦅 eagle',
        ],
    ],
    [
        'Food',
        'طعام',
        [
            '☕ coffee espresso caffeine',
            '🍵 tea green_tea',
            '🍺 beer pint',
            '🍻 beers cheers',
            '🥂 clink champagne celebrate',
            '🍷 wine',
            '🧋 bubble_tea boba',
            '🥤 soda drink',
            '🍕 pizza',
            '🍔 burger',
            '🌮 taco',
            '🍜 ramen noodles',
            '🍣 sushi',
            '🍩 doughnut donut',
            '🍪 cookie',
            '🎂 cake birthday',
            '🍫 chocolate',
            '🍿 popcorn watching',
            '🍎 apple',
            '🍌 banana',
            '🍉 watermelon',
            '🍓 strawberry',
            '🥑 avocado',
            '🌶️ hot_pepper spicy',
            '🧀 cheese',
            '🥐 croissant',
            '🍞 bread',
            '🥓 bacon',
            '🍳 cooking egg',
        ],
    ],
    [
        'Work',
        'عمل',
        [
            '💻 laptop computer code',
            '🖥️ desktop monitor',
            '⌨️ keyboard typing',
            '🖱️ computer_mouse click pointer',
            '💾 floppy save disk',
            '💿 disc cd',
            '📀 dvd',
            '🔌 plug power',
            '🔋 battery',
            '📱 phone mobile',
            '📡 satellite network signal',
            '🛰️ satellite_orbit space',
            '📦 package box deploy ship',
            '📁 folder directory',
            '📂 open_folder',
            '📄 page file document',
            '📋 clipboard copy paste',
            '📝 memo note write edit',
            '✏️ pencil write',
            '📌 pushpin pin',
            '📎 paperclip attach',
            '🔗 link url chain',
            '🔍 mag search find zoom',
            '🔑 key secret credential',
            '🔒 lock locked secure private',
            '🔓 unlock open public',
            '🛡️ shield security protect',
            '⚙️ gear settings config cog',
            '🔧 wrench fix tool',
            '🔨 hammer build',
            '🛠️ tools build repair',
            '🪛 screwdriver fix',
            '🧰 toolbox kit',
            '🧪 test_tube experiment testing',
            '🔬 microscope research inspect',
            '⏱️ stopwatch timing benchmark',
            '⏳ hourglass waiting slow',
            '⏰ alarm_clock deadline',
            '📅 calendar date schedule',
            '📈 chart_up growth metrics',
            '📉 chart_down loss regression',
            '📊 bar_chart data stats',
            '🗑️ trash delete bin remove',
            '♻️ recycle refactor reuse',
            '🏗️ construction building wip',
            '🚧 barrier wip blocked',
        ],
    ],
    [
        'Signals',
        'إشارات',
        [
            '🔥 fire hot burning lit',
            '✨ sparkles clean new shiny',
            '⭐ star favourite',
            '🌟 glowing_star',
            '⚡ zap fast lightning power',
            '💥 boom crash explosion',
            '💡 bulb idea insight',
            '🎉 tada party celebrate ship',
            '🎊 confetti celebrate',
            '🚀 rocket launch ship deploy fast',
            '🛸 ufo',
            '🏁 checkered_flag finish done',
            '🎯 dart target goal bullseye',
            '🏆 trophy win first',
            '🥇 first_place gold',
            '🎖️ medal honour',
            '🧨 dynamite explosive risky',
            '🪄 magic_wand magic fix',
            '🧯 extinguisher fire_fighting hotfix',
            '🚨 siren alert incident urgent',
            '📣 megaphone announce shout',
            '🔔 bell notify ping',
            '🔕 no_bell mute silent',
            '💬 speech_balloon comment chat',
            '💭 thought_balloon thinking',
            '❓ question what',
            '❗ exclamation important',
            '‼️ double_exclamation urgent',
            '✅ check_mark done yes passed green',
            '☑️ ballot_check done ticked',
            '❌ x no failed wrong',
            '⛔ no_entry blocked forbidden',
            '⚠️ warning caution careful',
            '🆗 ok_button fine',
            '🆕 new_button',
            '🔜 soon later',
            '➕ plus add',
            '➖ minus remove',
            '🔁 repeat loop retry',
            '🔀 shuffle random',
            '⏩ fast_forward skip',
            '⏸️ pause hold wait',
            '▶️ play start resume',
            '⏹️ stop halt',
            '🔄 refresh sync reload',
            '🌀 cyclone spiral confusion',
            '🌈 rainbow pride colour',
            '🌙 moon night late',
            '☀️ sun day morning',
            '🌧️ rain gloomy',
            '❄️ snowflake cold freeze',
            '🎵 music note',
            '🎧 headphones focus listening',
            '🎮 game_controller gaming play',
            '🎲 dice random luck',
            '🧩 puzzle piece fit',
            '🪑 chair sit seat',
            '🛏️ bed sleep tired',
            '🚿 shower clean',
            '🏠 house home',
            '🌍 earth world globe',
            '🗺️ map directions',
            '🧭 compass direction navigate',
            '⛵ sailboat sailing',
            '✈️ airplane flight travel',
            '🚗 car drive',
            '🚲 bike cycling',
            '🏃 running run go',
            '🧗 climbing effort struggle',
            '🏔️ mountain peak hard',
            '🕳️ hole rabbit_hole deep',
            '🪞 mirror reflection',
            '🧱 bricks wall blocker',
            '⛏️ pick mining dig',
            '🪵 wood log logs',
            '🌱 seedling growing new',
            '🌳 tree branch',
            '🍀 four_leaf_clover luck',
            '💤 zzz sleeping bored',
            '👑 crown king best',
            '🎩 top_hat classy',
            '🕶️ dark_glasses cool incognito',
            '🧢 cap hat',
            '👕 shirt tshirt',
            '🩹 adhesive_bandage patch bandaid hotfix',
            '💊 pill medicine',
            '🧬 dna genetics',
            '🦴 bone legacy',
            '🗿 moai stone stoic',
            '🧊 ice cube frozen cold',
            '🪤 mousetrap trap gotcha',
            '🧲 magnet attract',
            '🪃 boomerang returns regression',
            '🎪 circus chaos',
            '🎭 masks drama theatre',
            '🖼️ framed_picture image art',
            '🎨 art palette design colour',
            '📚 books docs reading learn',
            '📖 open_book read docs',
            '🎓 graduation learning degree',
            '🏅 medal_sports achievement',
            '🧵 thread sewing series',
            '🪡 needle fine_work detail',
            '🔭 telescope far vision',
            '🧿 nazar protection luck',
            '🕹️ joystick retro control',
        ],
    ],
];
/** Every group, in picker order. */
export const EMOJI_GROUPS = TABLE.map(([name, nameAr, rows]) => ({
    name,
    nameAr,
    emoji: rows.map((row) => {
        // "🎉 tada party celebrate ship": character, shortcode, then search keys.
        // A row with fewer than two words is a typo in the table, and becomes an
        // entry that can be seen and not found rather than a crash at import.
        const [c = row, code = row, ...rest] = row.split(' ');
        return { c, code, keys: [code, ...rest] };
    }),
}));
/** Every emoji, flat. */
export const EMOJI = EMOJI_GROUPS.flatMap((g) => g.emoji);
/** Shortcode to character, for `:tada:` and for the picker's search. */
export const EMOJI_BY_CODE = new Map(EMOJI.map((e) => [e.code, e.c]));
/* ── Faces ──────────────────────────────────────────────────────────────── */
/**
 * The avatars. Mostly drawn from the table above, chosen because a room of them
 * reads as a room of people: animals mostly, which are distinct from each
 * other at 16 pixels in a way that six yellow smileys are not.
 *
 * Order matters and must not be rearranged casually: a face nobody picked is
 * derived from the tag (see {@link faceFor}), so moving an entry moves which
 * face a person is wearing tomorrow.
 */
export const FACES = [
    '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵',
    '🐧', '🦆', '🦉', '🦇', '🐺', '🐴', '🦄', '🐝', '🐛', '🦋',
    '🐌', '🐞', '🕷️', '🐢', '🐍', '🦎', '🦖', '🐙', '🦑', '🦀',
    '🐡', '🐬', '🐳', '🦈', '🐊', '🐘', '🦒', '🦔', '🐑', '🐓',
    '🦅', '🐶', '🐱', '🐭', '🐹', '🐰', '👻', '👽', '🤖', '🤡',
    '💀', '🗿', '🦴', '🧿', '🍄', '🌵',
];
const FACE_SET = new Set(FACES);
/**
 * A face claimed by a browser, or null.
 *
 * The narrow gate that the wire depends on. A member's `avatar` is either one
 * of these characters or an https URL, and only the Worker may put a URL
 * there, from the account it read on the upgrade. If a browser could name a
 * URL instead, anyone could hang an image of their choosing in everyone
 * else's member list and read the room's traffic off their own server, so
 * the field a browser sends is checked against the set and nothing else.
 */
export function cleanFace(raw) {
    return typeof raw === 'string' && FACE_SET.has(raw) ? raw : null;
}
/** True for one of the faces, as opposed to an account's picture URL. */
export function isFace(raw) {
    return typeof raw === 'string' && FACE_SET.has(raw);
}
/**
 * The face somebody has before they have chosen one, from their tag.
 *
 * Everyone has a face from their first message, without being asked to pick,
 * and it is the same face on every device a signed-in person opens: the tag
 * is derived from the account, not the browser. The tag is four hex
 * characters (`tagFor`); anything else falls on the first face rather than
 * on undefined.
 */
export function faceFor(tag) {
    const n = Number.parseInt(tag.slice(0, 4), 16);
    const i = Number.isFinite(n) ? Math.abs(n) % FACES.length : 0;
    return FACES[i] ?? FACES[0];
}
/* ── Writing them ───────────────────────────────────────────────────────── */
const SHORTCODE = /:([a-z0-9_+-]{1,24}):/gi;
/**
 * `:tada:` becomes 🎉, and `:)` becomes 🙂.
 *
 * The composer runs this as the learner types, so what is sent is the
 * character rather than the code — nothing on the wire needs to know this
 * function exists, and a tab that never calls it shows exactly what was sent.
 * An unknown code is left alone: `:30:` in "back at :30:" is not an emoji,
 * and neither is anything else the table has never heard of.
 *
 * The emoticons are matched only as whole tokens, after a space or at the
 * start, because `:(` is a face in "well :(" and part of a path in
 * "sed s:(:[:g". `:/` is deliberately not one of them, whatever it looks
 * like, because every URL on earth contains it.
 */
export function expandShortcodes(text) {
    return text
        .replace(SHORTCODE, (whole, code) => EMOJI_BY_CODE.get(code.toLowerCase()) ?? whole)
        .replace(/(^|\s)(:-?\)|:-?\(|;-?\)|:-?D|:-?P|:'\(|<3|\bxD)(?=\s|$)/g, (whole, before, face) => {
        const found = EMOTICONS[face.toLowerCase()];
        return found ? `${before}${found}` : whole;
    });
}
const EMOTICONS = {
    ':)': '🙂',
    ':-)': '🙂',
    ':(': '🙁',
    ':-(': '🙁',
    ';)': '😉',
    ';-)': '😉',
    ':d': '😄',
    ':-d': '😄',
    ':p': '😛',
    ':-p': '😛',
    ":'(": '😢',
    '<3': '❤️',
    xd: '😆',
};
/**
 * How many emoji, if the message is nothing but emoji.
 *
 * Zero for anything with words in it. The list draws a short run of them
 * large, the way every chat does, because "🎉" as a reply is a gesture and
 * setting a gesture in body copy makes it look like a typo.
 */
// One emoji: a pictograph, any modifiers and variation selector on it, then
// any number of the same joined on with a zero-width joiner — which is what
// makes 👩‍💻 one emoji and not three. Written with escapes, because the two
// characters that matter here are invisible in an editor.
const SEQUENCE = /\p{Extended_Pictographic}(?:[\u{1F3FB}-\u{1F3FF}]|\uFE0F)*(?:\u200D\p{Extended_Pictographic}(?:[\u{1F3FB}-\u{1F3FF}]|\uFE0F)*)*/gu;
export function emojiOnly(text) {
    const trimmed = text.trim();
    if (trimmed === '')
        return 0;
    if (trimmed.replace(SEQUENCE, '').trim() !== '')
        return 0;
    return trimmed.match(SEQUENCE)?.length ?? 0;
}
/** What the picker offers for a search, in table order. Empty query, everything. */
export function searchEmoji(query) {
    const q = query.trim().toLowerCase().replace(/^:+|:+$/g, '');
    if (q === '')
        return EMOJI;
    const starts = EMOJI.filter((e) => e.keys.some((k) => k.startsWith(q)));
    if (starts.length > 0)
        return starts;
    return EMOJI.filter((e) => e.keys.some((k) => k.includes(q)));
}
//# sourceMappingURL=emoji.js.map